<template>
  <main class="shared-products-page">
    <header class="shared-shell-header">
      <div>
        <p>External lab access</p>
        <h1>Shared Products</h1>
      </div>
      <div class="lab-code-pill">
        <span>{{ appStore.state.activeUser.labId.institute.name }}</span>
        <strong>{{ appStore.state.activeUser.labId.name }}</strong>
        <em>{{ appStore.state.activeUser.labId.code }}</em>
      </div>
    </header>

    <section class="shared-stage">
      <aside class="external-user-panel">
        <header class="panel-header">
          <div>
            <p>External members</p>
            <h2>Other labs</h2>
          </div>
          <span>{{ externalUsers.length }}</span>
        </header>

        <div class="external-user-list">
          <button
            v-for="(user, index) in externalUsers"
            :key="user.userId.id"
            :ref="'userContainer' + index"
            type="button"
            class="external-user-card"
            @click="loadUser(user.userId.id, index)"
          >
            <span
              class="external-avatar"
              :style="{
                backgroundImage: `url(${
                  user.userId.photo
                    ? appStore.state.hostname + '/img/users/' + user.userId.photo
                    : 'https://d1muf25xaso8hp.cloudfront.net/https%3A%2F%2Fs3.amazonaws.com%2Fappforest_uf%2Ff1583584638543x692525241529171000%2Fperson-placeholder-male-5.jpg?w=192&h=202&auto=compress&dpr=1.25&fit=max'
                })`,
              }"
            ></span>
            <span class="external-user-copy">
              <strong>{{ user.userId.name }}</strong>
              <small>{{ user.userId.labId.name }}</small>
            </span>
            <span class="material-symbols-outlined" aria-hidden="true">chevron_right</span>
          </button>

          <article v-if="externalUsers.length == 0" class="empty-state">
            No external lab members.
          </article>
        </div>
      </aside>

      <section class="external-detail-panel">
        <article v-if="!selectedUser" class="select-empty-state">
          <span class="material-symbols-outlined" aria-hidden="true">share</span>
          <strong v-if="externalUsers.length > 0">Select an external member</strong>
          <strong v-else>No shared products yet</strong>
          <p>External access is managed per product level so web-only sharing can stay separate from canonical lab ownership.</p>
        </article>

        <article v-show="selectedUser" class="selected-user-workspace">
          <header class="selected-user-header">
            <div class="selected-identity">
              <span
                class="selected-avatar"
                :style="{
                  backgroundImage: `url(${
                    selectedUser?.userId?.photo
                      ? appStore.state.hostname + '/img/users/' + selectedUser?.userId?.photo
                      : 'https://d1muf25xaso8hp.cloudfront.net/https%3A%2F%2Fs3.amazonaws.com%2Fappforest_uf%2Ff1583584638543x692525241529171000%2Fperson-placeholder-male-5.jpg?w=192&h=202&auto=compress&dpr=1.25&fit=max'
                  })`,
                }"
              ></span>
              <div>
                <p>{{ selectedUser?.userId?.labId?.name }}</p>
                <h2>{{ selectedUser?.userId.name }}</h2>
                <small>{{ selectedUser?.userId.email }}</small>
              </div>
            </div>

            <div class="selected-actions">
              <label class="edit-toggle" for="editUser">
                <input id="editUser" v-model="editUser" type="checkbox" />
                <span>Edit access</span>
              </label>
              <button
                v-if="editUser"
                ref="saveUserChanges"
                type="button"
                class="save-command"
                @click="saveUser(selectedUser?.id)"
              >
                Save
              </button>
            </div>
          </header>

          <section class="admin-notes-panel">
            <label class="remove-access-toggle" for="userIsActive">
              <input
                id="userIsActive"
                ref="userIsActive"
                :disabled="
                  !editUser ||
                  (selectedUser?.userId.role == 'lab-admin' &&
                    appStore.state.activeUser.labId == selectedUser.labId)
                "
                type="checkbox"
                @change="notifyNotSaved"
              />
              <span>Remove all access</span>
            </label>
            <label class="notes-field">
              <span>Admin notes</span>
              <textarea
                ref="adminNotes"
                v-model="selectedUserNotes"
                :disabled="!editUser"
                placeholder="Context for this external member"
                rows="4"
                @input="updateAdminNotesFn"
              ></textarea>
            </label>
          </section>

          <section class="access-panel">
            <header class="access-header">
              <div>
                <p>Custom Product Access</p>
                <h3>Scoped inventory grants</h3>
              </div>
            </header>

            <div class="access-lock-overlay" v-if="
              !editUser ||
              (selectedUser?.userId.role == 'lab-admin' &&
                appStore.state.activeUser.labId == selectedUser?.userId.labId)
            "></div>

            <div class="access-grid">
              <section class="access-column">
                <h4>Dewars</h4>
                <article
                  v-for="(dewar, index) in selectedUser?.customDewarAccess"
                  :key="dewar.dewar?.id"
                  class="access-item"
                >
                  <div
                    class="closestToLabel access-label"
                    :data-itemid="dewar.dewar?.id"
                    :data-userid="selectedUser?.userId.id"
                    data-itemtype="dewar"
                  >
                    <strong>{{ dewar.dewar?.customId ?? "Dewar" + appStore.state.twoDigitsPlease(index + 1) }}</strong>
                    <small>{{ dewar.dewar?.title || "Dewar access" }}</small>
                  </div>
                  <custom-item
                    v-model:custom-access="dewarAccessCustom[index]"
                    :selected="dewar.access"
                    item-type="dewar"
                    :index="index + 1"
                    @val-change="updateCustomChangeContainer"
                  ></custom-item>
                </article>
              </section>

              <section class="access-column">
                <h4>Racks</h4>
                <article
                  v-for="(rack, index) in selectedUser?.customRackAccess"
                  :key="rack.rack?.id"
                  class="access-item"
                >
                  <div
                    class="closestToLabel access-label"
                    :data-itemid="rack.rack?.id"
                    :data-userid="selectedUser?.userId.id"
                    data-itemtype="rack"
                  >
                    <strong>{{ rack.rack?.serialNo }}</strong>
                    <small>{{ rack.rack?.title || rack.rack?.customId || "Rack access" }}</small>
                  </div>
                  <custom-item
                    v-model:custom-access="rackAccessCustom[index]"
                    :selected="rack.access"
                    item-type="rack"
                    :index="index + 1"
                    @val-change="updateCustomChangeContainer"
                  ></custom-item>
                </article>
              </section>

              <section class="access-column">
                <h4>Pucks</h4>
                <article
                  v-for="(puck, index) in selectedUser?.customPuckAccess"
                  :key="puck.puck?.id"
                  class="access-item"
                >
                  <div
                    class="closestToLabel access-label"
                    :data-itemid="puck.puck?.id"
                    :data-userid="selectedUser?.userId.id"
                    data-itemtype="puck"
                  >
                    <strong>{{ puck.puck?.serialNo }}</strong>
                    <small>{{ puck.puck?.title || puck.puck?.customId || "Puck access" }}</small>
                  </div>
                  <custom-item
                    v-model:custom-access="puckAccessCustom[index]"
                    :selected="puck.access"
                    item-type="puck"
                    :index="index + 1"
                    @val-change="updateCustomChangeContainer($event)"
                  ></custom-item>
                </article>
              </section>

              <section class="access-column">
                <h4>Boxes</h4>
                <article
                  v-for="(box, index) in selectedUser?.customBoxAccess"
                  :key="box.box?.id"
                  class="access-item"
                >
                  <div
                    class="closestToLabel access-label"
                    :data-itemid="box.box?.id"
                    :data-userid="selectedUser?.userId.id"
                    data-itemtype="box"
                  >
                    <strong>{{ box.box?.puckId.serialNo }}-{{ appStore.state.twoDigitsPlease(box.box?.wellNo) }}</strong>
                    <small>{{ box.box?.title || box.box?.customId || "Box access" }}</small>
                  </div>
                  <custom-item
                    v-model:custom-access="boxAccessCustom[index]"
                    :selected="box.access"
                    item-type="box"
                    :index="index + 1"
                    @val-change="updateCustomChangeContainer"
                  ></custom-item>
                </article>
              </section>
            </div>
          </section>
        </article>
      </section>
    </section>
  </main>
</template>
<script>
import CustomItem from "./group-admin-page/CustomItem.vue";
export default {
  components: { CustomItem },
  data() {
    return {
      lastClickedUser: null,
      dewarAccess: null,
      rackAccess: null,
      puckAccess: null,
      boxAccess: null,
      dewarAccessTemp: [],
      rackAccessTemp: [],
      puckAccessTemp: [],
      boxAccessTemp: [],
      dewarAccessCustom: [],
      rackAccessCustom: [],
      puckAccessCustom: [],
      boxAccessCustom: [],
      updateAdminNotes: false,
      selectedUserId: null,
      editUser: null,
      selectedUserNotes: "",
      activeAccessPanel: "custom",
      changedCustomItems: [],
    };
  },
  // watch: {
  //   externalUsers() {
  //     for (const [index, user] of this.externalUsers.entries()) {
  //       this.dewarAccessTemp[index] = user.globalDewarAccess;
  //       this.rackAccessTemp[index] = user.globalRackAccess;
  //       this.puckAccessTemp[index] = user.globalPuckAccess;
  //       this.boxAccessTemp[index] = user.globalBoxAccess;
  //     }
  //   },
  // },
  computed: {
    selectedUser() {
      const user = this.appStore.state.externalusers.filter((user) => {
        return user.userId.id == this.selectedUserId;
      })[0];

      return user;
    },
    externalUsers() {
      return this.appStore.state.externalusers;
    },
  },

  methods: {
    updateAdminNotesFn() {
      this.updateAdminNotes = true;
      this.notifyNotSaved();
    },
    updateCustomChangeContainer(ev) {
      const itemid =
        ev.target.parentElement.parentElement.previousElementSibling.dataset
          .itemid;
      const userid =
        ev.target.parentElement.parentElement.previousElementSibling.dataset
          .userid;
      const itemtype =
        ev.target.parentElement.parentElement.previousElementSibling.dataset
          .itemtype;

      var found = false;
      for (var i = 0; i < this.changedCustomItems.length; i++) {
        if (this.changedCustomItems[i].itemid == itemid) {
          found = true;
          this.changedCustomItems[i].customaccess = ev.target.value;
          break;
        }
      }

      if (!found)
        this.changedCustomItems.push({
          itemid,
          userid,
          itemtype,
          customaccess: ev.target.value,
        });

      console.log(this.changedCustomItems);
      this.notifyNotSaved();
    },
    notifyNotSaved() {
      if (this.$refs.saveUserChanges) {
        this.$refs.saveUserChanges.style.outline = "2px red dashed";
      }
    },
    loadUser(userId, containerIndex) {
      console.log(containerIndex);
      this.selectedUserId = userId;
      if (this.lastClickedUser)
        this.lastClickedUser.style.backgroundColor = "white";

      const refValue = this.$refs["userContainer" + containerIndex];
      this.lastClickedUser = Array.isArray(refValue) ? refValue[0] : refValue;
      if (this.lastClickedUser) {
        this.lastClickedUser.style.backgroundColor = "rgb(228, 240, 228)";
      }

      this.selectedUserNotes = this.selectedUser?.adminNotes;
      console.log(this.selectedUser);
    },
    saveUser(idforremoval) {
      // const body = {
      //   adminNotes: this.$refs.adminNotes.value,
      // };

      if (this.$refs.userIsActive.checked) {
        //rem,ove user
        // body.role = "temp";
        // this.selectedUser = null;
        // this.$refs.userIsActive.checked = "true";
        console.log(idforremoval);
        const remove = confirm(
          "Are you sure you want to remove all access for this user?"
        );
        if (!remove) {
          this.$refs.userIsActive.checked = false;
          return;
        }

        //fetch request

        const arrayOfUsersCustomDewarIdsToRemove = [];
        this.selectedUser?.customDewarAccess.forEach((item) => {
          arrayOfUsersCustomDewarIdsToRemove.push(item.dewar.id);
        });
        const arrayOfUsersCustomRackIdsToRemove = [];
        this.selectedUser?.customRackAccess.forEach((item) => {
          arrayOfUsersCustomRackIdsToRemove.push(item.rack.id);
        });
        const arrayOfUsersCustomPuckIdsToRemove = [];
        this.selectedUser?.customPuckAccess.forEach((item) => {
          arrayOfUsersCustomPuckIdsToRemove.push(item.puck.id);
        });

        const arrayOfUsersCustomBoxIdsToRemove = [];
        this.selectedUser?.customBoxAccess.forEach((item) => {
          arrayOfUsersCustomBoxIdsToRemove.push(item.box.id);
        });

        const body = {
          userId: this.selectedUser?.userId.id,
          customDewarsToRemove: arrayOfUsersCustomDewarIdsToRemove,
          customRacksToRemove: arrayOfUsersCustomRackIdsToRemove,
          customPucksToRemove: arrayOfUsersCustomPuckIdsToRemove,
          customBoxesToRemove: arrayOfUsersCustomBoxIdsToRemove,
        };
        let url = `${this.appStore.state.hostname}/api/v1/externaluser/removeall/${idforremoval}`;
        const request = new Request(url, {
          credentials: "include",
          method: "PATCH",
          body: JSON.stringify(body),

          headers: { "content-type": "application/json" },
        });

        fetch(request)
          .then((resp) => resp.json())
          .then((data) => {
            if (data.status != "success") throw new Error(data.message);
            else {
              console.log("dgaaggads");
              this.$refs.saveUserChanges.style.outline = "";
              this.getUsersForLab();
            }
          })
          .catch((err) => {
            console.log(err);
          });

        return;
      }

      let adminnotesbody = { adminNotes: this.selectedUserNotes };
      if (this.updateAdminNotes) {
        this.appStore.dispatch("updateItem", {
          body: adminnotesbody,
          item: "externaluser",
          objid: idforremoval,
        });
      }

      this.changedCustomItems.forEach((obj, idx, array) => {
        const { itemid, itemtype, customaccess, userid } = obj;

        if (idx !== array.length - 1) {
          this.appStore.state.manageUsersCustomAccessFnc(
            itemtype,
            itemid,
            customaccess,
            userid,
            true
          );
        } else {
          this.appStore.state
            .manageUsersCustomAccessFnc(
              itemtype,
              itemid,
              customaccess,
              userid,
              true
            )

            .then(() => {
              console.log("here");
              this.getUsersForLab();
            })

            .catch((err) => console.log(err));
        }
      });

      this.$refs.saveUserChanges.style.outline = "";
      this.updateAdminNotes = false;
    },
    approveUser(userId, userLab, index) {
      let proceed = true;

      if (
        this.dewarAccessTemp[index] == null ||
        this.dewarAccessTemp[index] == "2ndnull"
      ) {
        this.dewarAccessTemp[index] = "2ndnull";
        proceed = false;
      }
      if (
        this.rackAccessTemp[index] == null ||
        this.rackAccessTemp[index] == "2ndnull"
      ) {
        this.rackAccessTemp[index] = "2ndnull";
        proceed = false;
      }
      if (
        this.puckAccessTemp[index] == null ||
        this.puckAccessTemp[index] == "2ndnull"
      ) {
        this.puckAccessTemp[index] = "2ndnull";
        proceed = false;
      }
      if (
        this.boxAccessTemp[index] == null ||
        this.boxAccessTemp[index] == "2ndnull"
      ) {
        this.boxAccessTemp[index] = "2ndnull";
        proceed = false;
      }

      if (proceed == false) return;

      const body = {
        globalDewarAccess: this.dewarAccessTemp[index],
        globalRackAccess: this.rackAccessTemp[index],
        globalPuckAccess: this.puckAccessTemp[index],
        globalBoxAccess: this.boxAccessTemp[index],
        role: "user",
      };

      //need to make approval reactive. run get users for lab after dispatch complete
      //need to do delete
      console.log(userLab);
      this.appStore.dispatch("updateItem", {
        body,
        labid: userLab,
        item: "users",
        objid: userId,
      });
    },

    deleteUser(userId) {
      const deleteUser = confirm("Are you sure you want to delete this user?");
      if (!deleteUser) {
        return;
      }
      this.appStore.dispatch("deleteItem", {
        item: "users",
        objid: userId,
      });
    },

    getUsersForLab() {
      //when a user selects a lab from update lab

      const labid = this.appStore.state.activeUser.labId.id;

      this.appStore.dispatch("getAdminStuff", {
        url: "externalusers",
        labid,
      });
    },
  },
  mounted() {
    this.getUsersForLab();
  },
};
</script>

<style scoped>
.shared-products-page {
  display: grid;
  min-width: 0;
  min-height: calc(100vh - 24px);
  align-content: start;
  background: #fff;
  color: #172033;
  font-family: Montserrat, Inter, system-ui, sans-serif;
}

.shared-shell-header,
.panel-header,
.selected-user-header,
.access-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.shared-shell-header {
  padding: 14px 18px;
  border-bottom: 1px solid #dbe3ef;
  background: #fff;
}

.shared-shell-header p,
.panel-header p,
.selected-identity p,
.access-header p {
  margin: 0;
  color: #64748b;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.shared-shell-header h1,
.panel-header h2,
.selected-identity h2,
.access-header h3 {
  margin: 2px 0 0;
  color: #172033;
  line-height: 1.15;
}

.shared-shell-header h1 {
  font-size: 20px;
}

.lab-code-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  max-width: min(520px, 55vw);
  padding: 0 12px;
  border: 1px solid #dbe3ef;
  border-radius: 999px;
  background: #f8fafc;
  color: #526173;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.lab-code-pill strong {
  overflow: hidden;
  color: #172033;
  text-overflow: ellipsis;
}

.lab-code-pill em {
  padding: 2px 8px;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-style: normal;
  font-weight: 900;
}

.shared-stage {
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 12px;
  min-width: 0;
  padding: 12px;
}

.external-user-panel,
.external-detail-panel,
.access-panel,
.admin-notes-panel {
  min-width: 0;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #fff;
}

.panel-header {
  padding: 12px;
  border-bottom: 1px solid #dbe3ef;
}

.panel-header h2 {
  font-size: 18px;
}

.panel-header > span {
  min-width: 30px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #e7eef7;
  color: #335c8b;
  font-size: 12px;
  font-weight: 900;
  text-align: center;
}

.external-user-list {
  display: grid;
  gap: 8px;
  padding: 10px;
}

.external-user-card {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 58px;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #fbfdff;
  color: #172033;
  text-align: left;
  cursor: pointer;
}

.external-user-card:hover {
  border-color: #9db4d0;
  background: #f8fafc;
}

.external-avatar,
.selected-avatar {
  display: block;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background-color: #e7eef7;
  background-position: center;
  background-size: cover;
}

.external-avatar {
  width: 42px;
  height: 42px;
}

.external-user-copy {
  min-width: 0;
}

.external-user-copy strong,
.external-user-copy small,
.access-label strong,
.access-label small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.external-user-copy strong {
  color: #172033;
  font-size: 13px;
  font-weight: 900;
  text-transform: capitalize;
}

.external-user-copy small {
  margin-top: 2px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
}

.external-user-card .material-symbols-outlined {
  color: #94a3b8;
  font-size: 18px;
}

.select-empty-state,
.empty-state {
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 32px 18px;
  color: #64748b;
  text-align: center;
}

.empty-state {
  padding: 18px;
  border: 1px dashed #cbd5e1;
  border-radius: 7px;
  background: #f8fafc;
  font-size: 13px;
  font-weight: 800;
}

.select-empty-state .material-symbols-outlined {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 999px;
  background: #e7eef7;
  color: #335c8b;
}

.select-empty-state strong {
  color: #172033;
  font-size: 18px;
}

.select-empty-state p {
  max-width: 460px;
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.45;
}

.selected-user-workspace {
  display: grid;
  gap: 12px;
  padding: 12px;
}

.selected-user-header {
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.selected-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.selected-avatar {
  width: 58px;
  height: 58px;
  flex: 0 0 auto;
}

.selected-identity h2 {
  font-size: 22px;
  text-transform: capitalize;
}

.selected-identity small {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
}

.selected-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.edit-toggle,
.remove-access-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #334155;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
}

.edit-toggle input,
.remove-access-toggle input {
  width: 16px;
  height: 16px;
  margin: 0;
}

.save-command {
  min-height: 34px;
  padding: 0 14px;
  border: 1px solid #0f766e;
  border-radius: 6px;
  background: #0f766e;
  color: #fff;
  font: inherit;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
}

.admin-notes-panel {
  display: grid;
  grid-template-columns: minmax(170px, 0.35fr) minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  padding: 12px;
  background: #f8fafc;
}

.notes-field {
  display: grid;
  gap: 6px;
  min-width: 0;
  color: #526173;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.notes-field textarea {
  min-height: 96px;
  width: 100%;
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid #9db4d0;
  border-radius: 7px;
  background: #fff;
  color: #172033;
  font: inherit;
  font-size: 13px;
  resize: vertical;
  text-transform: none;
}

.notes-field textarea:focus {
  outline: none;
  border-color: #6889b3;
  box-shadow: 0 0 0 3px rgb(51 92 139 / 0.12);
}

.access-panel {
  position: relative;
  overflow: hidden;
}

.access-header {
  padding: 12px;
  border-bottom: 1px solid #dbe3ef;
}

.access-header h3 {
  font-size: 18px;
}

.access-lock-overlay {
  position: absolute;
  inset: 55px 0 0;
  z-index: 5;
  background: rgb(248 250 252 / 0.62);
  pointer-events: all;
}

.access-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding: 10px;
}

.access-column {
  display: grid;
  align-content: start;
  gap: 8px;
  min-width: 0;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #f8fafc;
}

.access-column h4 {
  margin: 0;
  color: #172033;
  font-size: 13px;
  font-weight: 900;
}

.access-item {
  display: grid;
  gap: 7px;
  min-width: 0;
  padding: 9px;
  border: 1px solid #dbe3ef;
  border-radius: 7px;
  background: #fff;
}

.access-label {
  min-width: 0;
}

.access-label strong {
  color: #172033;
  font-size: 12px;
  font-weight: 900;
}

.access-label small {
  margin-top: 2px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
}

@media (max-width: 1180px) {
  .shared-stage,
  .admin-notes-panel {
    grid-template-columns: 1fr;
  }

  .external-user-list {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }

  .access-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .shared-shell-header,
  .selected-user-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .lab-code-pill {
    max-width: 100%;
    flex-wrap: wrap;
    border-radius: 8px;
    white-space: normal;
  }

  .access-grid {
    grid-template-columns: 1fr;
  }
}
</style>
